import Typography from "@mui/material/Typography";
import {useTheme} from '@mui/material/styles';
import {styled} from '@mui/material';
import React, {memo, useMemo} from "react";
import {C014StageCarouselPictureSettings} from "../../../common/constants/pictureSettings";
import {usePreviewContext} from "../../../context/PreviewContext";
import useWindowSize from "../../../utility/useWindowSize";
import {getAltText} from "../../fundamentals/CCOPicture";
import CCOResponsivePicture from "../../fundamentals/CCOResponsivePicture";
import PreviewElementBoxDrawer from "../../preview/PreviewElementBoxDrawer";
import {metaDataProperty} from "../../preview/previewMetaData";
import { CCOStageTextBox } from "../../fundamentals/CCOStageTextBox";
import {SosTeaserType} from "../../types";
import CCOAiGeneratedImageLabel, {useAiGeneratedImageLabel} from "../../fundamentals/CCOAiGeneratedImageLabel";

const HeroImage = styled("div")(({theme}) => ({
    position: "relative",
    height: '338px',
    [theme.breakpoints.up('sm')]: {height: '338px'},
    [theme.breakpoints.up('md')]: {height: '386px'},
    [theme.breakpoints.up('lg')]: {height: '514px'},
    [theme.breakpoints.up('xl')]: {height: '658px'},
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    overflow: "hidden",
    textAlign: "left"
}));

const ImageOverlay = styled("div")(() => ({
    position: "absolute",
    top: 0,
    bottom: 0,
    right: 0,
    left: 0
}));

const pictureStyleConst = {
    objectFit: "cover" as const,
    width: "100%",
    height: "100%",
    position: "absolute" as const
};

const baseImageStyleConst = {
    objectFit: "cover" as const,
    width: "100%",
    height: "100%",
};

interface TextContentProps {
    teaserTitle: string | null | undefined;
    isPreview: boolean;
    itemId: string;
    activateH1?: boolean;
}

const TextContent = memo(
    function TextContent({teaserTitle, isPreview, itemId, activateH1}: TextContentProps) {
        return (
            <Typography
                variant="helveticaPageTitle"
                component={activateH1 ? 'h1' : 'div'}
                sx={{m: 0, p: 0, '& span': {hyphens: 'none'}}}
            >
            <span {...metaDataProperty(isPreview, 'properties.teaserTitle', itemId)}>
                {teaserTitle}
            </span>
            </Typography>
        );
    });

interface Props {
    item: SosTeaserType;
}

function C088HelveticaStageTeaser(props: Props) {
    const theme = useTheme();
    const picture = props.item?.picture ?? undefined;
    const stageTeaserPositioning = picture?.mediaSettings?.positionForStageteaser;
    const isBlackBackground = props.item?.teaserSettings?.titleOnBlackBackground;
    const pictureAlt = getAltText(picture?.alt, picture?.title);
    const aiLabel = useAiGeneratedImageLabel(picture, pictureAlt);

    const {greaterThanWindowMedium, lowerThanWindowMedium} = useWindowSize();
    const isPreview = usePreviewContext().isPreview;

    const stageTeaserPositionStyle = useMemo(() => (stageTeaserPositioning
            ? {
                height: "auto",
                minHeight: "100%",
                width: "100%",
                position: "absolute",
                top: stageTeaserPositioning === "top" ? "0" : undefined,
                bottom: stageTeaserPositioning === "bottom" ? "0" : undefined
            }
            : {}
    ), [stageTeaserPositioning]);

    function renderTextContent() {
        return (
            <CCOStageTextBox
                blackBg={isBlackBackground}
                sx={{
                    boxSizing: 'content-box',
                    paddingBottom: '26px',
                    paddingTop: {xs: '30px'},
                    paddingRight: {sm: '73px!important'},
                    minWidth: {
                        xs: 0,
                        lg: '472px',
                        xl: '730px'
                    },
                    [theme.breakpoints.up('xl')]: {
                        width: '730px',
                        height: '386px',
                        paddingLeft: '260px',
                    },
                }}
            >
                <TextContent
                    teaserTitle={props.item.teaserTitle}
                    isPreview={isPreview}
                    itemId={props.item.id}
                    activateH1={!!props.item?.teaserSettings?.activateH1}
                />
            </CCOStageTextBox>
        );
    }

    return (
        <PreviewElementBoxDrawer propertyId={props.item.id}>
            <div data-cypress="helveticaStageTeaser">
                <HeroImage>
                    {picture &&
                        <>
                            <CCOResponsivePicture
                                dataCypress={"stageTeaserImage"}
                                pictureSetting={C014StageCarouselPictureSettings}
                                picture={picture}
                                hasPriority={!stageTeaserPositioning}
                                alt={aiLabel?.imageAlt ?? pictureAlt}
                                imageStyle={{...baseImageStyleConst, ...stageTeaserPositionStyle}}
                                pictureStyle={pictureStyleConst}
                            />
                            <ImageOverlay/>
                            {aiLabel && <CCOAiGeneratedImageLabel label={aiLabel} variant='tile'/>}
                        </>
                    }
                    {greaterThanWindowMedium && renderTextContent()}
                </HeroImage>
                {lowerThanWindowMedium && renderTextContent()}
            </div>
        </PreviewElementBoxDrawer>
    );
}

export default C088HelveticaStageTeaser;
